import { useState, useEffect } from 'react';
import WizardLayout from './WizardLayout';
import { useWizard } from '../../contexts/WizardContext';

import { useProfile } from '../../contexts/ProfileContext';
import { createMemoriesFromWizardStep, createWizardMemories } from '../../utils/wizardMemoryTemplates';

export default function StepChallenges({ navigate, embedded = false, draft: externalDraft, setDraft: setExternalDraft }) {
  // Use either external draft (for embedded mode) or context draft (for wizard mode)
  const wizardContext = useWizard();
  const { refresh } = useProfile();
  
  const draft = embedded ? externalDraft : wizardContext.draft;
  const setDraft = embedded ? setExternalDraft : wizardContext.setDraft;
  
  const [challenges, setChallenges] = useState(draft?.challenges || []);
  const [, setSavedMemories] = useState([]);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  
  // Keep local state in sync with draft changes (important for embedded mode)
  useEffect(() => {
    if (draft) {
      setChallenges(draft.challenges || []);
    }
  }, [draft]);

  // Comprehensive challenge options covering typical symptoms for
  // ADHD, Autism, Asperger, and Tourettes
  const challengeOptions = [
    // Core challenges (original)
    'Konsentrasjon',
    'Fokus',
    'Søvn',
    'Språk og kommunikasjon',
    'Motorikk',
    'Sosialt samspill',
    'Atferd',
    'Læring',
    // ADHD-related
    'Impulsivitet',
    'Hyperaktivitet',
    'Planlegging og organisering',
    // Autism/Asperger-related
    'Sansefølsomhet',
    'Rutiner og overganger',
    'Angst',
    // Tourettes/OCD-related
    'Tics',
    'Tvang',
    'Trang',
    // Daily life
    'Følelsesregulering',
    'Selvstendighet i hverdagen'
  ];

  const toggleChallenge = challenge => {
    const newChallenges = challenges.includes(challenge)
      ? challenges.filter(c => c !== challenge)
      : [...challenges, challenge];
    
    setChallenges(newChallenges);
    
    // In embedded mode, update the draft immediately
    if (embedded) {
      setDraft({
        ...draft,
        challenges: newChallenges
      });
    }
  };

  const skipWizard = async () => {
    try {
      // Create memories if user has provided any challenges
      if (challenges && challenges.length > 0) {
        const stepData = {
          challenges,
          // Include child info from previous steps for entity mapping
          alias: draft?.child?.alias || '',
          birthYear: draft?.child?.birthYear || null
        };
        
        const memories = createMemoriesFromWizardStep('challenges', stepData);
        
        if (memories.length > 0) {
          const result = await createWizardMemories(memories);
          if (result.success) {
            setSavedMemories(memories);
            setShowSaveConfirmation(true);
            setTimeout(() => setShowSaveConfirmation(false), 3000);
          }
        }
      }

      // Update profile with wizardSkipped flag (comment out profile save for now)
      // TODO: Remove profile saving entirely once migration is complete
      /*
      await authFetch('/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...draft,
          wizardSkipped: true,
          challenges: challenges || []
        })
      });
      */
      
      // Wait until shared context is up-to-date
      await refresh();
      
      // Guard now sees correct flags
      navigate('/home', { replace: true });
    } catch (error) {
      console.error('Error skipping wizard:', error);
      alert('Det oppstod en feil. Vennligst prøv igjen.');
    }
  };

  const onNext = async ({ skip } = {}) => {
    if (embedded) return;
    
    if (skip) {
      await skipWizard();
      return;
    }
    
    try {
      // Create memories from current step data
      const stepData = {
        challenges,
        // Include child info from previous steps for entity mapping
        alias: draft?.child?.alias || '',
        birthYear: draft?.child?.birthYear || null
      };
      
      const memories = createMemoriesFromWizardStep('challenges', stepData);
      
      if (memories.length > 0) {
        const result = await createWizardMemories(memories);
        if (result.success) {
          setSavedMemories(memories);
          setShowSaveConfirmation(true);
          setTimeout(() => setShowSaveConfirmation(false), 3000);
        }
      }
      
      // Update local wizard state for flow continuity
      setDraft({
        ...draft,
        challenges
      });
      
      navigate('/onboarding/current-needs');
    } catch (error) {
      console.error('Error in onNext:', error);
      alert('Det oppstod en feil. Vennligst prøv igjen.');
    }
  };


  // The form content
  const formContent = (
    <>
      <h2 className="text-xl font-semibold mb-6">Hvilke utfordringer har barnet?</h2>
      

      {/* Save confirmation */}
      {showSaveConfirmation && (
        <div className="mb-4 p-3 bg-sage-50 border border-sage-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-sage-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sage-800 font-medium">Lagret: </span>
            <span className="text-sage-700">
              {challenges.join(', ')}
            </span>
          </div>
        </div>
      )}

      <div className="mb-6">
        <p className="mb-3">Velg alle som passer *</p>
        <div className="flex flex-wrap gap-2">
          {challengeOptions.map(challenge => (
            <button
              key={challenge}
              type="button"
              onClick={() => toggleChallenge(challenge)}
              className={`px-3 py-1 rounded-full transition-colors ${challenges.includes(challenge) ? 'bg-primary text-white' : 'bg-neutral-200 text-text-secondary'}`}
            >
              {challenge}
            </button>
          ))}
        </div>
      </div>

    </>
  );

  // For embedded mode, just return the form content
  if (embedded) {
    return (
      <div className="border-b pb-8">
        {formContent}
      </div>
    );
  }

  // For wizard mode, wrap in WizardLayout
  return (
    <WizardLayout
      step={3}
      total={5}
      nextDisabled={challenges.length === 0}
      onNext={onNext}
    >
      {formContent}
    </WizardLayout>
  );
}
